/** Settings (design §13) — vertical tab rail + content (Region & Tax,
 *  Numbering, e-Invoicing, Branding, Users & Roles). Region, tax rates and tax
 *  registrations load live from their services; users reflect the current
 *  session (full user management lives in the platform admin).
 *  GSTIN lives in tax registrations (one per jurisdiction, each with its own
 *  effectivity), never as a single field — see taxRegistrations.service. */
import React, { useCallback, useEffect, useState } from 'react';
import { Platform, Pressable, Text, TextInput, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../utils/responsive';
import { useSession } from '../context/sessionContext';
import SkIcon from '../components/skIcon';
import { initials } from '../utils/format';
import { getOrgProfile, listTaxRates } from '../services/billing/accountant.service';
import {
  REG_TYPES,
  createTaxRegistration,
  endTaxRegistration,
  listTaxRegistrations,
  type RegType,
  type TaxRegistration,
} from '../services/billing/taxRegistrations.service';

const TABS = [
  { id: 'region', label: 'Region & Tax', icon: 'building' },
  { id: 'numbering', label: 'Numbering', icon: 'fileText' },
  { id: 'einvoice', label: 'e-Invoicing', icon: 'percent' },
  { id: 'branding', label: 'Branding', icon: 'sliders' },
  { id: 'users', label: 'Users & Roles', icon: 'settings' },
];

// Document numbering patterns are region-pack defaults; live "next number"
// comes from the backend on issue.
const NUMBERING = [
  ['Tax Invoice', 'INV-#####', 'FY'],
  ['Quote', 'QUO-#####', 'FY'],
  ['Credit Note', 'CN-#####', 'FY'],
  ['Bill (internal)', 'BILL-#####', 'Never'],
];

const Settings: React.FC = () => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const { isWide } = useResponsive();
  const { user, role } = useSession();
  const [tab, setTab] = useState('region');
  const [regionPack, setRegionPack] = useState('');
  const [taxRates, setTaxRates] = useState<any[]>([]);
  const [registrations, setRegistrations] = useState<TaxRegistration[]>([]);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regType, setRegType] = useState<RegType>('GSTIN');
  const [regValue, setRegValue] = useState('');
  const [jurisdiction, setJurisdiction] = useState('IN');
  const [expiresOn, setExpiresOn] = useState('');
  const isAdmin = role === 'ADMIN';

  const loadRegistrations = useCallback(() => {
    listTaxRegistrations()
      .then(setRegistrations)
      .catch(() => {});
  }, []);

  const saveRegistration = useCallback(async () => {
    setSaving(true);
    try {
      await createTaxRegistration({
        jurisdiction_code: jurisdiction,
        reg_type: regType,
        reg_value: regValue.trim(),
        ...(regType === 'LUT' && expiresOn ? { expires_on: expiresOn } : {}),
      });
      setAdding(false);
      setRegValue('');
      setExpiresOn('');
      loadRegistrations();
    } catch {
      // the service layer already surfaced the error
    } finally {
      setSaving(false);
    }
  }, [jurisdiction, regType, regValue, expiresOn, loadRegistrations]);

  const endRegistration = useCallback(
    async (id: string) => {
      try {
        await endTaxRegistration(id);
        loadRegistrations();
      } catch {
        // already surfaced
      }
    },
    [loadRegistrations],
  );

  useEffect(() => {
    let alive = true;
    getOrgProfile()
      .then((p: any) => {
        if (!alive) return;
        setRegionPack(p?.region_pack_code || '');
      })
      .catch(() => {});
    loadRegistrations();
    listTaxRates()
      .then((rs: any[]) => { if (alive && Array.isArray(rs)) setTaxRates(rs); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 8 } as const;
  const label = { fontFamily: font.sans, fontSize: 11, color: colors.ink3, textTransform: 'uppercase' as const, letterSpacing: 0.4 };

  const field = { borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 5, height: 34, paddingHorizontal: 11, fontFamily: font.sans, fontSize: 13, color: colors.ink, ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) };
  const RegionTax = () => (
    <View style={{ gap: 14 }}>
      {/* Region pack card */}
      <View style={[card, { padding: 16 }]}>
        <Text style={{ fontFamily: font.sansSemibold, fontSize: 14, fontWeight: '600', color: colors.ink }}>Region pack</Text>
        <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3, marginTop: 2, marginBottom: 14 }}>Packs are data + rules, not code. ADMIN can override individual values.</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, borderRadius: 8 }}>
          <View style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center' }}>
            <SkIcon name="building" size={17} color={colors.accent} strokeWidth={2} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.accentInk }}>{regionPack === 'IN_GST' ? 'India GST · IN_GST' : regionPack || 'No region pack active'}</Text>
            <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink3, marginTop: 1 }}>CGST/SGST/IGST · GSTIN · TDS · e-invoicing · lakh/crore grouping</Text>
          </View>
          {!!regionPack && (
            <View style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: colors.accent }}>
              <Text style={{ fontSize: 9, fontWeight: '700', letterSpacing: 0.5, color: '#fff' }}>ACTIVE</Text>
            </View>
          )}
        </View>
      </View>

      {/* Tax registrations card — the SSoT for GSTIN/TRN/LUT. One row per
          jurisdiction, because India registers state-wise and an LUT expires. */}
      <View style={[card, { overflow: 'hidden' }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderColor: colors.line }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 14, fontWeight: '600', color: colors.ink }}>Tax registrations</Text>
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3, marginTop: 2 }}>Your GSTIN per state. Printed on every tax invoice you issue.</Text>
          </View>
          {isAdmin && !adding && (
            <Pressable onPress={() => setAdding(true)} style={{ height: 30, paddingHorizontal: 11, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2 }}>Add registration</Text>
            </Pressable>
          )}
        </View>

        {adding && (
          <View style={{ padding: 16, borderBottomWidth: 1, borderColor: colors.line, gap: 12 }}>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <View style={{ flex: 1 }}>
                <Text style={[label, { textTransform: 'none', letterSpacing: 0, marginBottom: 4 }]}>Type</Text>
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  {REG_TYPES.map((t) => (
                    <Pressable key={t} onPress={() => setRegType(t)} style={{ paddingHorizontal: 9, height: 34, borderRadius: 5, borderWidth: 1, justifyContent: 'center', borderColor: regType === t ? colors.accent : colors.line, backgroundColor: regType === t ? colors.accentSoft : colors.paper }}>
                      <Text style={{ fontFamily: font.sans, fontSize: 12, color: regType === t ? colors.accentInk : colors.ink2 }}>{t}</Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            </View>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <View style={{ flex: 2 }}>
                <Text style={[label, { textTransform: 'none', letterSpacing: 0, marginBottom: 4 }]}>{regType}</Text>
                <TextInput value={regValue} onChangeText={setRegValue} autoCapitalize="characters" placeholder={regType === 'GSTIN' ? 'e.g. 27AAACU1234M1Z9' : 'Registration number'} placeholderTextColor={colors.ink4} style={field} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[label, { textTransform: 'none', letterSpacing: 0, marginBottom: 4 }]}>Jurisdiction</Text>
                <TextInput value={jurisdiction} onChangeText={(v) => setJurisdiction(v.toUpperCase())} autoCapitalize="characters" maxLength={2} placeholder="IN" placeholderTextColor={colors.ink4} style={field} />
              </View>
              {/* The API requires an expiry for LUT — it is renewed every 15 March. */}
              {regType === 'LUT' && (
                <View style={{ flex: 1 }}>
                  <Text style={[label, { textTransform: 'none', letterSpacing: 0, marginBottom: 4 }]}>Expires on</Text>
                  <TextInput value={expiresOn} onChangeText={setExpiresOn} placeholder="YYYY-MM-DD" placeholderTextColor={colors.ink4} style={field} />
                </View>
              )}
            </View>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Pressable
                disabled={saving || !regValue.trim() || jurisdiction.length !== 2}
                onPress={saveRegistration}
                style={{ height: 34, paddingHorizontal: 16, borderRadius: 5, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accent, opacity: saving || !regValue.trim() || jurisdiction.length !== 2 ? 0.5 : 1 }}
              >
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 12, fontWeight: '600', color: '#fff' }}>{saving ? 'Saving…' : 'Save'}</Text>
              </Pressable>
              <Pressable onPress={() => setAdding(false)} style={{ height: 34, paddingHorizontal: 14, borderRadius: 5, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2 }}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        )}

        {registrations.length === 0 && !adding ? (
          <View style={{ padding: 16 }}>
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4 }}>No registrations yet. Invoices will be issued without a GSTIN until one is added.</Text>
          </View>
        ) : (
          registrations.map((r) => (
            <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 16, borderBottomWidth: 1, borderColor: colors.line }}>
              <Text style={{ width: 70, fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.ink }}>{r.reg_type}</Text>
              <Text style={{ flex: 1, fontFamily: font.mono, fontSize: 12, color: colors.ink2 }}>{r.reg_value}</Text>
              <Text style={{ width: 48, fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>{r.jurisdiction_code}</Text>
              <View style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: r.is_active ? colors.accent : colors.line, marginRight: 10 }}>
                <Text style={{ fontSize: 9, fontWeight: '700', letterSpacing: 0.5, color: r.is_active ? '#fff' : colors.ink3 }}>{r.is_active ? 'ACTIVE' : 'ENDED'}</Text>
              </View>
              {isAdmin && r.is_active && (
                <Pressable onPress={() => endRegistration(r.id)}>
                  <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>End</Text>
                </Pressable>
              )}
            </View>
          ))
        )}
      </View>

      {/* Tax rates card */}
      <View style={[card, { overflow: 'hidden' }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderColor: colors.line }}>
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 14, fontWeight: '600', color: colors.ink }}>Tax rates</Text>
          <Pressable style={{ height: 30, paddingHorizontal: 11, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2 }}>Add rate</Text>
          </Pressable>
        </View>
        {taxRates.length === 0 ? (
          <View style={{ padding: 16 }}><Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4 }}>Tax rates load from the active region pack.</Text></View>
        ) : (
          taxRates.map((t) => (
            <View key={t.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 16, borderBottomWidth: 1, borderColor: colors.line }}>
              <Text style={{ width: 130, fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.ink }}>{t.name}</Text>
              <Text style={{ width: 60, fontFamily: font.mono, fontSize: 12, color: colors.ink2 }}>{t.rate_percent}%</Text>
              <Text style={{ flex: 1, fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>{t.tax_kind}</Text>
              <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
                <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink3 }}>{t.is_active === false ? 'inactive' : 'active'}</Text>
              </View>
            </View>
          ))
        )}
      </View>
    </View>
  );

  const Numbering = () => (
    <View style={[card, { overflow: 'hidden' }]}>
      {NUMBERING.map(([s, p, r]) => (
        <View key={s} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 14, borderBottomWidth: 1, borderColor: colors.line }}>
          <Text style={{ flex: 1, fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.ink }}>{s}</Text>
          <Text style={{ width: 130, fontFamily: font.mono, fontSize: 12, color: colors.ink3 }}>{p}</Text>
          <Text style={{ width: 70, fontFamily: font.sans, fontSize: 12, color: colors.ink3 }}>{r}</Text>
        </View>
      ))}
    </View>
  );

  const Users = () => {
    const name = user ? `${user.firstName} ${user.lastName}`.trim() : 'You';
    const email = user?.email || '';
    const rc = role === 'ADMIN' ? [colors.accentInk, colors.accentSoft, colors.accentLine] : role === 'EDITOR' ? [colors.info, colors.infoSoft, colors.infoLine] : [colors.ink3, colors.paper2, colors.line];
    return (
      <View style={{ gap: 12 }}>
        <View style={[card, { overflow: 'hidden' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 11, paddingHorizontal: 14 }}>
            <View style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: colors.paper3, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontSize: 10, fontWeight: '600', color: colors.ink2, fontFamily: font.sansSemibold }}>{initials(name)}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: colors.ink }}>{name}</Text>
              {!!email && <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4 }}>{email}</Text>}
            </View>
            <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: rc[1], borderWidth: 1, borderColor: rc[2] }}>
              <Text style={{ fontFamily: font.sans, fontSize: 11, fontWeight: '500', color: rc[0] }}>{role}</Text>
            </View>
          </View>
        </View>
        <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3, lineHeight: 18 }}>Invite teammates and manage roles in the platform admin console.</Text>
      </View>
    );
  };

  const Simple = ({ text }: { text: string }) => (
    <View style={[card, { padding: 20 }]}><Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3, lineHeight: 20 }}>{text}</Text></View>
  );

  return (
    <View style={{ maxWidth: 980, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16, flexDirection: isWide ? 'row' : 'column', gap: 16 }}>
      <View style={[card, { width: isWide ? 206 : undefined, padding: 8 }]}>
        {TABS.map((t) => {
          const a = t.id === tab;
          return (
            <Pressable key={t.id} onPress={() => setTab(t.id)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 9, borderRadius: 7, backgroundColor: a ? colors.accentSoft : 'transparent', marginBottom: 1 }}>
              <SkIcon name={t.icon} size={15} color={a ? colors.accentInk : colors.ink3} />
              <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: a ? '600' : '500', color: a ? colors.accentInk : colors.ink2 }}>{t.label}</Text>
            </Pressable>
          );
        })}
      </View>
      <View style={{ flex: 1 }}>
        {/* Called, not mounted: as <RegionTax /> it was a NEW component type on
            every render, so typing one character remounted the GSTIN box and
            threw the cursor out of it. */}
        {tab === 'region' && RegionTax()}
        {tab === 'numbering' && <Numbering />}
        {tab === 'einvoice' && <Simple text="e-Invoicing enabled · auto-queue on issue. Reporting window 30 days · threshold ₹5 Cr. Past-window invoices route to the guided fix interstitial." />}
        {tab === 'branding' && <Simple text="Logo, legal name, accent color (Indigo / Green / Rust / Violet), and PDF footer text." />}
        {tab === 'users' && <Users />}
      </View>
    </View>
  );
};

export default Settings;
